import ipponApi from "@/config/axios";
import { getClientID } from "./authUtils";

/**
 * Presigned URLを取得する
 */
export async function getPresignedUploadUrl(
  file: File,
  propertyId: string,
  useOcrPath: boolean = false
): Promise<{ uploadUrl: string; s3Key: string }> {
  const clientId = getClientID();

  if (!clientId) {
    throw new Error("クライアントIDが取得できません");
  }

  const response = await ipponApi.post("/api/v1/property-upload-url", {
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    propertyId: propertyId,
    useOcrPath: useOcrPath,
  });

  const { uploadUrl, s3Key } = response.data.data;

  if (!uploadUrl || !s3Key) {
    throw new Error("Presigned URLの取得に失敗しました");
  }

  return { uploadUrl, s3Key };
}

/**
 * S3に直接ファイルをアップロードする
 */
export async function uploadToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`S3へのアップロードに失敗しました: ${response.statusText}`);
  }
}

/**
 * 1つのファイルをPresigned URLでアップロードしてS3キーを返す
 */
export async function uploadFileWithPresignedUrl(
  file: File,
  propertyId: string,
  useOcrPath: boolean = false
): Promise<string> {
  // 1. Presigned URLを取得
  const { uploadUrl, s3Key } = await getPresignedUploadUrl(
    file,
    propertyId,
    useOcrPath
  );

  // 2. S3に直接アップロード
  await uploadToS3(uploadUrl, file);

  // 3. S3キーを返す
  return s3Key;
}

/**
 * 複数ファイルをアップロードしてS3キーの配列を返す
 */
export async function uploadFilesToS3(
  files: File[],
  propertyId: string,
  useOcrPath: boolean = false
): Promise<string[]> {
  if (!files || files.length === 0) {
    return [];
  }

  // 並列アップロード（Promise.all）
  const uploadPromises = files.map((file) =>
    uploadFileWithPresignedUrl(file, propertyId, useOcrPath)
  );

  return await Promise.all(uploadPromises);
}

/**
 * ファイルまたは既存URL/S3キーの配列を処理して、統合した配列を返す
 * @param items - File, 文字列URL, S3キー, ImageDataオブジェクトの混在配列
 * @param propertyId - 物件ID
 * @param useOcrPath - OCR用パスを使用するか
 * @returns 統合されたS3キーまたはURLの配列
 */
export async function processFileArray(
  items: any[],
  propertyId: string,
  useOcrPath: boolean = false
): Promise<string[]> {
  if (!items || items.length === 0) {
    return [];
  }

  const results: string[] = [];

  for (const item of items) {
    // Fileオブジェクトの場合（新規アップロード）
    if (item instanceof File) {
      const s3Key = await uploadFileWithPresignedUrl(
        item,
        propertyId,
        useOcrPath
      );
      results.push(s3Key);
    }
    // 既存URL（文字列）の場合
    else if (typeof item === "string") {
      results.push(item);
    }
    // CustomFileUploaderが返す既存URL形式: {id: "...", data: "url", isUrl: true}
    else if (item && item.isUrl && item.data && typeof item.data === "string") {
      results.push(item.data);
    }
    // urlプロパティを持つオブジェクト（既存ファイル）の場合
    else if (
      item &&
      typeof item === "object" &&
      item.url &&
      typeof item.url === "string"
    ) {
      results.push(item.url);
    }
    // ImageData形式（CustomFileUploaderから新規アップロード）の場合
    // 必ず originalFileName と mimeType を持つ
    else if (
      item &&
      item.data &&
      typeof item.data === "string" &&
      item.originalFileName &&
      item.mimeType
    ) {
      // base64データかどうかチェック
      const isBase64 = item.data.startsWith("data:");

      if (isBase64) {
        // base64をBlobに変換してFileオブジェクトを作成
        try {
          const base64Data = item.data.split(",")[1];
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: item.mimeType });
          const file = new File([blob], item.originalFileName, {
            type: item.mimeType,
          });

          // S3にアップロード
          const s3Key = await uploadFileWithPresignedUrl(
            file,
            propertyId,
            useOcrPath
          );
          results.push(s3Key);
        } catch (error) {
          console.error("Failed to convert base64 to file:", error);
          // エラーの場合はスキップ
        }
      } else {
        // 既にS3キーまたはURLの場合はそのまま使用
        results.push(item.data);
      }
    }
    // 既存データ形式: { data: "S3キーまたはURL" }（バックエンドから返される形式）
    // originalFileName と mimeType を持たない
    else if (
      item &&
      typeof item === "object" &&
      item.data &&
      typeof item.data === "string"
    ) {
      results.push(item.data);
    }
  }

  return results;
}

/**
 * クライアント用Presigned URLを取得する
 */
export async function getClientPresignedUploadUrl(
  file: File
): Promise<{ uploadUrl: string; s3Key: string }> {
  const clientId = getClientID();

  if (!clientId) {
    throw new Error("クライアントIDが取得できません");
  }

  const response = await ipponApi.post("/api/v1/client-upload-url", {
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
  });

  const { uploadUrl, s3Key } = response.data.data;

  if (!uploadUrl || !s3Key) {
    throw new Error("Presigned URLの取得に失敗しました");
  }

  return { uploadUrl, s3Key };
}

/**
 * クライアント用：1つのファイルをPresigned URLでアップロードしてS3キーを返す
 */
export async function uploadClientFileWithPresignedUrl(
  file: File
): Promise<string> {
  // 1. Presigned URLを取得
  const { uploadUrl, s3Key } = await getClientPresignedUploadUrl(file);

  // 2. S3に直接アップロード
  await uploadToS3(uploadUrl, file);

  // 3. S3キーを返す
  return s3Key;
}

/**
 * クライアント用：ファイルまたは既存URL/S3キーの配列を処理して、統合した配列を返す
 * @param items - File, 文字列URL, S3キー, ImageDataオブジェクトの混在配列
 * @returns 統合されたS3キーまたはURLの配列
 */
export async function processClientFileArray(items: any[]): Promise<string[]> {
  if (!items || items.length === 0) {
    return [];
  }

  const results: string[] = [];

  for (const item of items) {
    // Fileオブジェクトの場合（新規アップロード）
    if (item instanceof File) {
      const s3Key = await uploadClientFileWithPresignedUrl(item);
      results.push(s3Key);
    }
    // 既存URL（文字列）の場合
    else if (typeof item === "string") {
      results.push(item);
    }
    // CustomFileUploaderが返す既存URL形式: {id: "...", data: "url", isUrl: true}
    else if (item && item.isUrl && item.data && typeof item.data === "string") {
      results.push(item.data);
    }
    // urlプロパティを持つオブジェクト（既存ファイル）の場合
    else if (
      item &&
      typeof item === "object" &&
      item.url &&
      typeof item.url === "string"
    ) {
      results.push(item.url);
    }
    // ImageData形式（CustomFileUploaderから新規アップロード）の場合
    // 必ず originalFileName と mimeType を持つ
    else if (
      item &&
      item.data &&
      typeof item.data === "string" &&
      item.originalFileName &&
      item.mimeType
    ) {
      // base64データかどうかチェック
      const isBase64 = item.data.startsWith("data:");

      if (isBase64) {
        // base64をBlobに変換してFileオブジェクトを作成
        try {
          const base64Data = item.data.split(",")[1];
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: item.mimeType });
          const file = new File([blob], item.originalFileName, {
            type: item.mimeType,
          });

          // S3にアップロード
          const s3Key = await uploadClientFileWithPresignedUrl(file);
          results.push(s3Key);
        } catch (error) {
          console.error("Failed to convert base64 to file:", error);
          // エラーの場合はスキップ
        }
      } else {
        // 既にS3キーまたはURLの場合はそのまま使用
        results.push(item.data);
      }
    }
    // 既存データ形式: { data: "S3キーまたはURL" }（バックエンドから返される形式）
    // originalFileName と mimeType を持たない
    else if (
      item &&
      typeof item === "object" &&
      item.data &&
      typeof item.data === "string"
    ) {
      results.push(item.data);
    }
  }

  return results;
}
